import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Shift } from '../../shifts/services/ShiftService';
import { CrmTask } from '../../sales/services/CrmTaskService';
import CrmTaskService from '../../sales/services/CrmTaskService';
import PlantafelService, { LaneTeam, WorkAreaConflict, TeamAsset, ProjectSite, JobDetail, ChecklistItem, ProjectMilestone, JobComment, JobAttachment } from '../services/PlantafelService';
import { useAuthContext } from '../../../context/AuthContext';
import { pdebugError } from '../utils/plantafelDebug';
import ShiftService from '../../shifts/services/ShiftService';

interface UseSidePanelStateArgs {
  shiftProp: Shift | null;
  task: CrmTask | null;
  allTeams: LaneTeam[];
  onClose: () => void;
  onRefresh: () => Promise<void>;
}

export function useSidePanelState({ shiftProp, task, allTeams, onClose, onRefresh }: UseSidePanelStateArgs) {
  const [localShift, setLocalShift] = useState<Shift | null>(shiftProp);
  useEffect(() => { setLocalShift(shiftProp); }, [shiftProp]);
  const shift = localShift;

  const [changingStatus, setChangingStatus] = useState(false);
  const [deletingShift, setDeletingShift] = useState(false);
  const [deletingJob, setDeletingJob] = useState(false);
  const [removingEmployee, setRemovingEmployee] = useState<number | null>(null);
  const [removingAsset, setRemovingAsset] = useState<number | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [unscheduling, setUnscheduling] = useState(false);
  const [editStartDate, setEditStartDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [savingTime, setSavingTime] = useState(false);
  const [originalTime, setOriginalTime] = useState({ startDate: '', startTime: '', endDate: '', endTime: '' });
  const [showComments, setShowComments] = useState(true);
  const [sites, setSites] = useState<ProjectSite[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [savingSite, setSavingSite] = useState(false);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [rescheduleConflicts, setRescheduleConflicts] = useState<WorkAreaConflict[]>([]);
  const [jobDetail, setJobDetail] = useState<JobDetail | null>(null);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [showChecklist, setShowChecklist] = useState(true);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [addingChecklistItem, setAddingChecklistItem] = useState(false);
  const [togglingItemId, setTogglingItemId] = useState<number | null>(null);
  const [expandedSubtaskId, setExpandedSubtaskId] = useState<number | null>(null);
  const [subtaskComments, setSubtaskComments] = useState<JobComment[]>([]);
  const [subtaskAttachments, setSubtaskAttachments] = useState<JobAttachment[]>([]);
  const [subtaskDataLoading, setSubtaskDataLoading] = useState(false);
  const [subtaskCommentText, setSubtaskCommentText] = useState('');
  const [subtaskPendingFiles, setSubtaskPendingFiles] = useState<File[]>([]);
  const [sendingSubtaskComment, setSendingSubtaskComment] = useState(false);
  const subtaskFileInputRef = useRef<HTMLInputElement>(null);
  const [projectMilestones, setProjectMilestones] = useState<ProjectMilestone[]>([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [savingMilestone, setSavingMilestone] = useState(false);
  const { user } = useAuthContext();
  const [jobComments, setJobComments] = useState<JobComment[]>([]);
  const [jobAttachments, setJobAttachments] = useState<JobAttachment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [newCommentText, setNewCommentText] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [sendingComment, setSendingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);
  const [attachingToCommentId, setAttachingToCommentId] = useState<number | null>(null);

  // Initialize time edit fields
  useEffect(() => {
    if (shift) {
      const start = new Date(shift.start_time);
      const end = new Date(shift.end_time);
      const fmtDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const fmtTime = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      const sd = fmtDate(start), st = fmtTime(start), ed = fmtDate(end), et = fmtTime(end);
      setEditStartDate(sd); setEditStartTime(st); setEditEndDate(ed); setEditEndTime(et);
      setOriginalTime({ startDate: sd, startTime: st, endDate: ed, endTime: et });
    }
  }, [shift]);

  const hasTimeChanged = editStartDate !== originalTime.startDate || editStartTime !== originalTime.startTime || editEndDate !== originalTime.endDate || editEndTime !== originalTime.endTime;
  const projectId = task?.project || (shift as any)?.project || (shift as any)?.project_id;

  // Load sites
  useEffect(() => {
    if (projectId && shift?.is_job_shift) {
      setSitesLoading(true);
      PlantafelService.fetchProjectSites(projectId)
        .then(data => {
          setSites(data);
          const currentSiteId = (shift as any)?.execution_site_id || (shift as any)?.execution_site || (shift as any)?.site_id || (task as any)?.execution_site_id;
          setSelectedSiteId(currentSiteId || null);
        })
        .catch(err => { pdebugError('Failed to load sites:', err); setSites([]); })
        .finally(() => setSitesLoading(false));
    } else { setSites([]); setSelectedSiteId(null); }
  }, [projectId, shift?.id, shift?.is_job_shift]);

  // Load milestones
  useEffect(() => {
    if (projectId && shift?.is_job_shift) {
      setMilestonesLoading(true);
      PlantafelService.fetchProjectMilestones(projectId, false)
        .then(data => setProjectMilestones(data))
        .catch(err => { pdebugError('Failed to load project milestones:', err); setProjectMilestones([]); })
        .finally(() => setMilestonesLoading(false));
    } else { setProjectMilestones([]); }
  }, [projectId, shift?.is_job_shift]);

  const crmTaskId = task?.id || shift?.crm_task || (shift as any)?.crm_task_id;
  const isJobShift = shift?.is_job_shift === true || !!crmTaskId;

  // Load job detail and checklist
  useEffect(() => {
    if (crmTaskId) {
      PlantafelService.fetchJobDetail(crmTaskId).then(data => setJobDetail(data)).catch(err => { pdebugError('Failed to load job detail:', err); setJobDetail(null); });
      setChecklistLoading(true);
      PlantafelService.fetchJobChecklist(crmTaskId).then(items => setChecklist(items)).catch(err => { pdebugError('Failed to load checklist:', err); setChecklist([]); }).finally(() => setChecklistLoading(false));
    } else { setJobDetail(null); setChecklist([]); }
  }, [crmTaskId]);

  // Load comments and attachments
  const shouldLoadJobData = !!crmTaskId;
  useEffect(() => {
    if (shouldLoadJobData) {
      setCommentsLoading(true);
      PlantafelService.fetchJobComments(crmTaskId!).then(c => setJobComments(c)).catch(err => { pdebugError('Failed to load job comments:', err); setJobComments([]); }).finally(() => setCommentsLoading(false));
      setAttachmentsLoading(true);
      PlantafelService.fetchJobAttachments(crmTaskId!).then(a => setJobAttachments(a)).catch(err => { pdebugError('Failed to load job attachments:', err); setJobAttachments([]); }).finally(() => setAttachmentsLoading(false));
    } else { setJobComments([]); setJobAttachments([]); }
  }, [crmTaskId, shouldLoadJobData]);

  const handleSendComment = useCallback(async () => {
    if (!crmTaskId || (!newCommentText.trim() && pendingFiles.length === 0)) return;
    setSendingComment(true);
    try {
      const commentText = newCommentText.trim() || (pendingFiles.length > 0 ? '(Dateianhang)' : '');
      const created = await PlantafelService.createJobComment(crmTaskId, commentText);
      setJobComments(prev => [...prev, created]);
      if (pendingFiles.length > 0) {
        const uploaded: JobAttachment[] = [];
        for (const file of pendingFiles) { try { const a = await PlantafelService.uploadJobAttachment(crmTaskId, file, created.id); uploaded.push({ ...a, comment_id: created.id }); } catch (e) { pdebugError('Failed to upload file:', e); } }
        if (uploaded.length > 0) setJobAttachments(prev => [...prev, ...uploaded]);
      }
      setNewCommentText(''); setPendingFiles([]);
      toast.success(pendingFiles.length > 0 ? 'Kommentar mit Anhängen gesendet' : 'Kommentar hinzugefügt');
    } catch (err) { pdebugError('Failed to create comment:', err); toast.error('Fehler beim Senden'); }
    finally { setSendingComment(false); }
  }, [crmTaskId, newCommentText, pendingFiles]);

  const handleAddPendingFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    setPendingFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    e.target.value = '';
  }, []);

  const handleRemovePendingFile = useCallback((index: number) => { setPendingFiles(prev => prev.filter((_, i) => i !== index)); }, []);

  const handleAttachToComment = useCallback(async (commentId: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!crmTaskId || !e.target.files?.length) return;
    setAttachingToCommentId(commentId);
    const files = Array.from(e.target.files);
    try {
      for (const file of files) { const a = await PlantafelService.uploadJobAttachment(crmTaskId, file, commentId); setJobAttachments(prev => [...prev, { ...a, comment_id: commentId }]); }
      toast.success(files.length > 1 ? `${files.length} Dateien angehängt` : 'Datei angehängt');
    } catch (err) { pdebugError('Failed to attach file:', err); toast.error('Fehler beim Anhängen'); }
    finally { setAttachingToCommentId(null); e.target.value = ''; }
  }, [crmTaskId]);

  const handleDeleteComment = useCallback(async (commentId: number) => {
    setDeletingCommentId(commentId);
    try { await PlantafelService.deleteJobComment(commentId); setJobComments(prev => prev.filter(c => c.id !== commentId)); toast.success('Kommentar gelöscht'); }
    catch (err) { pdebugError('Failed to delete comment:', err); toast.error('Fehler beim Löschen'); }
    finally { setDeletingCommentId(null); }
  }, []);

  const handleDeleteAttachment = useCallback(async (attachmentId: number) => {
    setDeletingAttachmentId(attachmentId);
    try { await PlantafelService.deleteJobAttachment(attachmentId); setJobAttachments(prev => prev.filter(a => a.id !== attachmentId)); toast.success('Datei gelöscht'); }
    catch (err) { pdebugError('Failed to delete attachment:', err); toast.error('Fehler beim Löschen'); }
    finally { setDeletingAttachmentId(null); }
  }, []);

  const handleToggleChecklistItem = useCallback(async (item: ChecklistItem) => {
    if (!crmTaskId) return;
    const previousMilestoneLabel = jobDetail?.payment_milestone_label || jobDetail?.ops_milestone_label;
    setTogglingItemId(item.id);
    try {
      const updated = await PlantafelService.toggleChecklistItem(crmTaskId, item.id, !item.is_done);
      setChecklist(prev => prev.map(i => i.id === item.id ? updated : i));
      const updatedChecklist = checklist.map(i => i.id === item.id ? updated : i);
      if (updatedChecklist.every(i => i.is_done) && updatedChecklist.length > 0) {
        const refreshedJob = await PlantafelService.fetchJobDetail(crmTaskId);
        if (refreshedJob) {
          const newLabel = refreshedJob.payment_milestone_label || refreshedJob.ops_milestone_label;
          if (previousMilestoneLabel && refreshedJob.checklist_progress?.percent === 100) {
            toast.success(`Checkliste vollständig! Milestone "${newLabel || previousMilestoneLabel}" bereit zur Rechnung.`, { autoClose: 5000 });
            const pId = refreshedJob.project_id || task?.project || (shift as any)?.project || (shift as any)?.project_id;
            if (pId) window.dispatchEvent(new CustomEvent('milestoneStatusChanged', { detail: { projectId: pId, milestoneLabel: newLabel || previousMilestoneLabel } }));
          }
          setJobDetail(refreshedJob);
        }
      }
    } catch (err) { pdebugError('Failed to toggle checklist item:', err); toast.error('Fehler beim Aktualisieren'); }
    finally { setTogglingItemId(null); }
  }, [crmTaskId, checklist, jobDetail]);

  const handleAddChecklistItem = useCallback(async () => {
    if (!crmTaskId || !newChecklistItem.trim()) return;
    setAddingChecklistItem(true);
    try { const created = await PlantafelService.createChecklistItem(crmTaskId, { text: newChecklistItem.trim(), order: checklist.length }); setChecklist(prev => [...prev, created]); setNewChecklistItem(''); }
    catch (err) { pdebugError('Failed to add checklist item:', err); toast.error('Fehler beim Hinzufügen'); }
    finally { setAddingChecklistItem(false); }
  }, [crmTaskId, newChecklistItem, checklist.length]);

  const handleDeleteChecklistItem = useCallback(async (itemId: number) => {
    if (!crmTaskId) return;
    try { await PlantafelService.deleteChecklistItem(crmTaskId, itemId); setChecklist(prev => prev.filter(i => i.id !== itemId)); if (expandedSubtaskId === itemId) setExpandedSubtaskId(null); }
    catch (err) { pdebugError('Failed to delete checklist item:', err); toast.error('Fehler beim Löschen'); }
  }, [crmTaskId, expandedSubtaskId]);

  const handleToggleSubtaskExpand = useCallback(async (itemId: number) => {
    if (expandedSubtaskId === itemId) { setExpandedSubtaskId(null); setSubtaskComments([]); setSubtaskAttachments([]); setSubtaskCommentText(''); setSubtaskPendingFiles([]); return; }
    setExpandedSubtaskId(itemId); setSubtaskDataLoading(true); setSubtaskCommentText(''); setSubtaskPendingFiles([]);
    try {
      const [comments, attachments] = await Promise.all([PlantafelService.fetchJobComments(crmTaskId!, itemId), PlantafelService.fetchJobAttachments(crmTaskId!, itemId)]);
      setSubtaskComments(comments); setSubtaskAttachments(attachments);
    } catch (err) { pdebugError('Failed to load subtask data:', err); }
    finally { setSubtaskDataLoading(false); }
  }, [crmTaskId, expandedSubtaskId]);

  const handleSendSubtaskComment = useCallback(async () => {
    if (!crmTaskId || !expandedSubtaskId || (!subtaskCommentText.trim() && subtaskPendingFiles.length === 0)) return;
    setSendingSubtaskComment(true);
    try {
      const text = subtaskCommentText.trim() || (subtaskPendingFiles.length > 0 ? '(Dateianhang)' : '');
      const created = await PlantafelService.createJobComment(crmTaskId, text, expandedSubtaskId);
      setSubtaskComments(prev => [...prev, { ...created, checklist_item_id: expandedSubtaskId }]);
      let uploadedCount = 0;
      for (const file of subtaskPendingFiles) {
        try { const a = await PlantafelService.uploadJobAttachment(crmTaskId, file, created.id, expandedSubtaskId); setSubtaskAttachments(prev => [...prev, { ...a, comment_id: created.id, checklist_item_id: expandedSubtaskId }]); uploadedCount++; }
        catch (uploadErr: any) { pdebugError('Failed to upload file:', uploadErr); toast.error(`Upload fehlgeschlagen: ${file.name} - ${uploadErr?.response?.status || uploadErr?.message}`); }
      }
      if (subtaskPendingFiles.length > 0 && uploadedCount === 0) toast.error('Keine Dateien konnten hochgeladen werden');
      setChecklist(prev => prev.map(item => item.id === expandedSubtaskId ? { ...item, comments_count: (item.comments_count || 0) + 1, attachments_count: (item.attachments_count || 0) + subtaskPendingFiles.length } : item));
      setSubtaskCommentText(''); setSubtaskPendingFiles([]);
      toast.success('Kommentar hinzugefügt');
    } catch (err) { pdebugError('Failed to send subtask comment:', err); toast.error('Fehler beim Senden'); }
    finally { setSendingSubtaskComment(false); }
  }, [crmTaskId, expandedSubtaskId, subtaskCommentText, subtaskPendingFiles]);

  const handleDeleteSubtaskComment = useCallback(async (commentId: number) => {
    try {
      await PlantafelService.deleteJobComment(commentId);
      setSubtaskComments(prev => prev.filter(c => c.id !== commentId));
      if (expandedSubtaskId) setChecklist(prev => prev.map(item => item.id === expandedSubtaskId ? { ...item, comments_count: Math.max(0, (item.comments_count || 0) - 1) } : item));
      toast.success('Kommentar gelöscht');
    } catch (err) { pdebugError('Failed to delete comment:', err); toast.error('Fehler beim Löschen'); }
  }, [expandedSubtaskId]);

  const handleDeleteSubtaskAttachment = useCallback(async (attachmentId: number) => {
    try {
      await PlantafelService.deleteJobAttachment(attachmentId);
      setSubtaskAttachments(prev => prev.filter(a => a.id !== attachmentId));
      if (expandedSubtaskId) setChecklist(prev => prev.map(item => item.id === expandedSubtaskId ? { ...item, attachments_count: Math.max(0, (item.attachments_count || 0) - 1) } : item));
      toast.success('Datei gelöscht');
    } catch (err) { pdebugError('Failed to delete attachment:', err); toast.error('Fehler beim Löschen'); }
  }, [expandedSubtaskId]);

  const handleSaveMilestone = useCallback(async (milestoneId: number | null) => {
    if (!crmTaskId) return;
    setSavingMilestone(true);
    try {
      await PlantafelService.updateJobMilestone(crmTaskId, milestoneId);
      const sel = milestoneId ? projectMilestones.find(m => m.id === milestoneId) : null;
      setJobDetail(prev => prev ? { ...prev, payment_milestone_id: milestoneId || undefined, payment_milestone_label: sel?.label || undefined } : null);
      toast.success('Milestone aktualisiert');
      if (projectId) window.dispatchEvent(new CustomEvent('milestoneStatusChanged', { detail: { projectId, milestoneLabel: sel?.label || '' } }));
    } catch (err: any) { pdebugError('Failed to update milestone:', err); toast.error(err?.response?.data?.detail || 'Fehler beim Aktualisieren'); }
    finally { setSavingMilestone(false); }
  }, [crmTaskId, projectMilestones, projectId]);

  const handleSaveSite = useCallback(async (siteId: number | null) => {
    if (!shift) return;
    const taskId = task?.id || shift?.crm_task || (shift as any)?.crm_task_id;
    if (!taskId) { toast.error('Job-ID nicht gefunden'); return; }
    setSavingSite(true);
    try { await PlantafelService.updateJobSite(taskId, siteId); setSelectedSiteId(siteId); toast.success('Standort aktualisiert'); }
    catch (err: any) { pdebugError('Failed to update site:', err); toast.error(err?.response?.data?.detail || 'Fehler beim Aktualisieren'); }
    finally { setSavingSite(false); }
  }, [shift, task]);

  const handleRemoveEmployee = useCallback(async (employeeId: number) => {
    if (!shift) return;
    setRemovingEmployee(employeeId);
    try {
      const assignment = (shift as any).assignments?.find((a: any) => a.employee === employeeId || a.employee_id === employeeId);
      if (assignment) await ShiftService.deleteShiftAssignment(assignment.id);
      toast.success('Mitarbeiter entfernt');
      setLocalShift(prev => { if (!prev) return prev; const cur = (prev as any).assigned_employees || []; return { ...prev, assigned_employees: cur.filter((e: any) => e.id !== employeeId && e.employee_id !== employeeId) } as Shift; });
      onRefresh().catch(err => pdebugError('Background refresh failed:', err));
    } catch (err) { pdebugError('Failed to remove employee:', err); toast.error('Fehler beim Entfernen'); }
    finally { setRemovingEmployee(null); }
  }, [shift, onRefresh]);

  const handleRemoveAsset = useCallback(async (assetId: number) => {
    if (!shift) return;
    setRemovingAsset(assetId);
    try {
      await PlantafelService.unassignAssetFromShift(shift.id, assetId);
      toast.success('Asset entfernt');
      setLocalShift(prev => { if (!prev) return prev; const cur = (prev as any).asset_assignments || []; return { ...prev, asset_assignments: cur.filter((a: any) => a.id !== assetId && a.asset_id !== assetId) } as Shift; });
      onRefresh().catch(err => pdebugError('Background refresh failed:', err));
    } catch (err) { pdebugError('Failed to remove asset:', err); toast.error('Fehler beim Entfernen'); }
    finally { setRemovingAsset(null); }
  }, [shift, onRefresh]);

  const handleMarkDone = useCallback(async () => {
    if (!task) return; setChangingStatus(true);
    try { await CrmTaskService.changeStatus(task.id, 'DONE'); toast.success('Task als erledigt markiert'); await onRefresh(); }
    catch (err) { pdebugError('Failed to mark task as done:', err); toast.error('Fehler beim Ändern des Status'); }
    finally { setChangingStatus(false); }
  }, [task, onRefresh]);

  const handleReopen = useCallback(async () => {
    if (!task) return; setChangingStatus(true);
    try { await CrmTaskService.changeStatus(task.id, 'OPEN'); toast.success('Task wieder geöffnet'); await onRefresh(); }
    catch (err) { pdebugError('Failed to reopen task:', err); toast.error('Fehler beim Ändern des Status'); }
    finally { setChangingStatus(false); }
  }, [task, onRefresh]);

  const handleDeleteShift = useCallback(async () => {
    if (!shift) return;
    const msg = shift.is_job_shift ? 'Job wirklich löschen?\n\nDer zugehörige Task und alle Kommentare werden ebenfalls gelöscht!' : 'Schicht wirklich löschen?';
    if (!window.confirm(msg)) return;
    setDeletingShift(true);
    try { await PlantafelService.deleteShift(shift.id); toast.success(shift.is_job_shift ? 'Job gelöscht' : 'Schicht gelöscht'); onClose(); await onRefresh(); }
    catch (err) { pdebugError('Failed to delete shift:', err); toast.error(shift.is_job_shift ? 'Fehler beim Löschen des Jobs' : 'Fehler beim Löschen der Schicht'); }
    finally { setDeletingShift(false); }
  }, [shift, onClose, onRefresh]);

  const handleDeleteJob = useCallback(async () => {
    if (!task) return;
    if (!window.confirm('Job wirklich löschen?\n\nAlle Daten werden unwiderruflich gelöscht!')) return;
    setDeletingJob(true);
    try { await PlantafelService.deleteJob(task.id); toast.success('Job gelöscht'); onClose(); await onRefresh(); }
    catch (err) { pdebugError('Failed to delete job:', err); toast.error('Fehler beim Löschen des Jobs'); }
    finally { setDeletingJob(false); }
  }, [task, onClose, onRefresh]);

  const handleReschedule = useCallback(async () => {
    if (!shift || !selectedTeamId) return;
    setRescheduling(true);
    try { await PlantafelService.rescheduleShift(shift.id, selectedTeamId, 'MOVE_ONLY'); toast.success('Schicht verschoben'); setSelectedTeamId(null); await onRefresh(); }
    catch (err: any) {
      pdebugError('Failed to reschedule shift:', err);
      if (err?.response?.status === 409) {
        const cd = err?.response?.data?.conflicts || err?.response?.data?.detail?.conflicts || [];
        const conflicts: WorkAreaConflict[] = Array.isArray(cd) ? cd.map((c: any) => ({ shift_id: c.shift_id || 0, task_title: c.task_title || c.title || 'Bestehender Job', start_time: c.start_time || c.start || '', end_time: c.end_time || c.end || '', overlap_minutes: c.overlap_minutes || c.overlap || 0 })) : [];
        if (conflicts.length > 0) { setRescheduleConflicts(conflicts); setConflictModalOpen(true); } else toast.error('Konflikt: Team hat bereits eine Schicht zu dieser Zeit');
      } else toast.error('Fehler beim Verschieben der Schicht');
    } finally { setRescheduling(false); }
  }, [shift, selectedTeamId, onRefresh]);

  const handleUnschedule = useCallback(async () => {
    if (!shift) return;
    if (!window.confirm('Shift wird entfernt, Job bleibt als Aufgabe im Backlog.')) return;
    setUnscheduling(true);
    try { await PlantafelService.unscheduleShift(shift.id); toast.success('Zurück ins Backlog verschoben'); onClose(); await onRefresh(); }
    catch (err) { pdebugError('Failed to unschedule shift:', err); toast.error('Fehler beim Entfernen der Schicht'); }
    finally { setUnscheduling(false); }
  }, [shift, onClose, onRefresh]);

  const handleSaveTime = useCallback(async () => {
    if (!shift) return;
    setSavingTime(true);
    try {
      const newStartTime = new Date(`${editStartDate}T${editStartTime}:00`).toISOString();
      const newEndTime = new Date(`${editEndDate}T${editEndTime}:00`).toISOString();
      await PlantafelService.updateShiftTime(shift.id, newStartTime, newEndTime);
      toast.success('Zeit aktualisiert');
      setOriginalTime({ startDate: editStartDate, startTime: editStartTime, endDate: editEndDate, endTime: editEndTime });
      await onRefresh();
    } catch (err: any) { pdebugError('Failed to update shift time:', err); toast.error(err?.response?.data?.detail || 'Fehler beim Aktualisieren'); }
    finally { setSavingTime(false); }
  }, [shift, editStartDate, editStartTime, editEndDate, editEndTime, onRefresh]);

  const closeConflictModal = useCallback(() => { setConflictModalOpen(false); setRescheduleConflicts([]); }, []);

  const getDayName = (dateStr: string): string => {
    if (!dateStr) return '';
    return ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][new Date(dateStr).getDay()];
  };

  const formatMultiDayDateTime = (startIso: string, endIso: string): string => {
    const start = new Date(startIso); const end = new Date(endIso);
    const sameDay = start.toDateString() === end.toDateString();
    const fmt = (d: Date) => {
      const dn = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
      return { dayName: dn[d.getDay()], date: `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}`, time: `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}` };
    };
    const s = fmt(start); const e = fmt(end);
    return sameDay ? `${s.dayName} ${s.date} ${s.time} \u2013 ${e.time}` : `${s.dayName} ${s.date} ${s.time} \u2192 ${e.dayName} ${e.date} ${e.time}`;
  };

  const statusColors = task ? PlantafelService.getStatusColor(task.status) : null;
  const priorityColors = task ? PlantafelService.getPriorityColor(task.priority) : null;
  const isDone = task?.status === 'DONE' || task?.status === 'CANCELLED';
  const hasTaskContext = task || (shift?.is_job_shift && shift?.crm_task);
  const assignedAssets: TeamAsset[] = (shift as any)?.asset_assignments || [];

  return {
    shift, localShift, setLocalShift,
    changingStatus, deletingShift, deletingJob,
    removingEmployee, removingAsset,
    selectedTeamId, setSelectedTeamId, rescheduling, unscheduling,
    editStartDate, editStartTime, editEndDate, editEndTime,
    setEditStartDate, setEditStartTime, setEditEndDate, setEditEndTime,
    savingTime, hasTimeChanged,
    showComments, setShowComments,
    sites, sitesLoading, selectedSiteId, savingSite,
    conflictModalOpen, rescheduleConflicts, closeConflictModal,
    jobDetail, checklist, checklistLoading, showChecklist, setShowChecklist,
    newChecklistItem, setNewChecklistItem, addingChecklistItem, togglingItemId,
    expandedSubtaskId, subtaskComments, subtaskAttachments, subtaskDataLoading,
    subtaskCommentText, setSubtaskCommentText, subtaskPendingFiles, setSubtaskPendingFiles,
    sendingSubtaskComment, subtaskFileInputRef,
    projectMilestones, milestonesLoading, savingMilestone,
    jobComments, jobAttachments, commentsLoading, attachmentsLoading,
    newCommentText, setNewCommentText, pendingFiles, sendingComment,
    deletingCommentId, deletingAttachmentId, attachingToCommentId,
    projectId, crmTaskId, isJobShift,
    statusColors, priorityColors, isDone, hasTaskContext, assignedAssets,
    handleSendComment, handleAddPendingFile, handleRemovePendingFile,
    handleAttachToComment, handleDeleteComment, handleDeleteAttachment,
    handleToggleChecklistItem, handleAddChecklistItem, handleDeleteChecklistItem,
    handleToggleSubtaskExpand, handleSendSubtaskComment,
    handleDeleteSubtaskComment, handleDeleteSubtaskAttachment,
    handleSaveMilestone, handleSaveSite,
    handleRemoveEmployee, handleRemoveAsset,
    handleMarkDone, handleReopen, handleDeleteShift, handleDeleteJob,
    handleReschedule, handleUnschedule, handleSaveTime,
    getDayName, formatMultiDayDateTime,
  };
}
